import { Basis, ConfidencePill, HonestState, type BasisKind } from "../components.js";
import type { ConfidenceBasis, DeliveryMode, RoadmapEntry } from "../api/useRoadmap.js";

/**
 * E8.14 (NX-2643) AC2/AC3/AC5 — the per-project row of the timeline: `Swimlane` (one lane per
 * project, AC2), `ModeAwareDeliveryMarker` (the mode-keyed pin/band/unsupported dispatch, AC3), and
 * `DeliveryConfidenceBar` (the p50→p85 span, reusing the forecast-ribbon's own visual grammar —
 * p50/p85 markers + a "today" anchor + the span's WIDTH as the honest uncertainty signal — design/07
 * §5/§7 — rather than a bespoke chart library). Every component here is presentational: it takes
 * already-fetched props and never fetches (AC5; the injected hook lives in `useRoadmap.ts` and is
 * read only by `Roadmap.tsx`).
 */

/** `timeZone: "UTC"` pins formatting to the API's own UTC date components — the browser's
 *  local offset would otherwise shift a `YYYY-MM-DD`/`...Z` date back a day for any US
 *  timezone (e.g. `2026-10-01` -> "Sep 30, 2026" in PDT). */
const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
  year: "numeric",
  timeZone: "UTC",
});
const formatDate = (iso: string): string => DATE_FMT.format(new Date(iso));

const basisKindOf = (basis: ConfidenceBasis | null): BasisKind =>
  basis === "throughput"
    ? "empirical"
    : basis === "estimation"
      ? "estimate"
      : basis === "hybrid"
        ? "hybrid"
        : "none";

/** Position `iso` as a 0–100 percentage of [start, end], clamped so an out-of-window date still
 *  renders at an edge rather than off-screen or NaN. */
function pct(iso: string, start: number, end: number): number {
  const t = new Date(iso).getTime();
  const span = Math.max(end - start, 1);
  const clamped = Math.min(Math.max(t, start), end);
  return ((clamped - start) / span) * 100;
}

export type MarkerVariant = "pin" | "band";

export interface DeliveryConfidenceBarProps {
  readonly p50Date: string;
  readonly p85Date: string;
  readonly windowStart: number;
  readonly windowEnd: number;
  readonly confidence: number | null;
  readonly variant: MarkerVariant;
}

/**
 * The p50→p85 confidence span. `variant="pin"` (date-driven / scope-driven) marks a single committed
 * landing point; `variant="band"` (continuous) never renders a pin — a continuous-mode project has no
 * single delivery date by design (ADR-017), so pinning one would be exactly the fabrication ADR-016
 * forbids. The two variants are dual-encoded (shape + accessible name), never colour-alone, and are
 * structurally distinct DOM (a `role="img"` pin vs a `role="img"` band) so a mode mix-up is visible
 * in the accessibility tree, not just in CSS.
 */
export function DeliveryConfidenceBar({
  p50Date,
  p85Date,
  windowStart,
  windowEnd,
  confidence,
  variant,
}: DeliveryConfidenceBarProps): React.ReactNode {
  const p50Pct = pct(p50Date, windowStart, windowEnd);
  const p85Pct = pct(p85Date, windowStart, windowEnd);
  const left = Math.min(p50Pct, p85Pct);
  const width = Math.max(Math.abs(p85Pct - p50Pct), 1.5);
  return (
    <div className={`roadmapbar ${variant}`}>
      <div className="track">
        <div className={`fill ${variant}`} style={{ left: `${left}%`, width: `${width}%` }} />
        {variant === "pin" ? (
          <span
            className="pin"
            role="img"
            aria-label={`Fixed-date pin: ${formatDate(p50Date)}`}
            style={{ left: `${p50Pct}%` }}
          >
            📍
          </span>
        ) : (
          <span
            className="band"
            role="img"
            aria-label={`Continuous band: through ${formatDate(p85Date)}`}
            style={{ left: `${left}%`, width: `${width}%` }}
          >
            ▬▬▶
          </span>
        )}
      </div>
      <div className="dates meta">
        p50 <b>{formatDate(p50Date)}</b> → p85 <b>{formatDate(p85Date)}</b>{" "}
        <ConfidencePill value={confidence} />
      </div>
    </div>
  );
}

/** date-driven and scope-driven both land on a committed date (ADR-017 04b §3 rows 1/2) — a pin.
 *  continuous never does (row 3) — a band. Keyed dispatch (E5.10a precedent, delivery-health.ts's
 *  own `DELIVERY_HEALTH_STRATEGIES` map) rather than an if/switch chain: adding a fourth
 *  `DeliveryMode` means adding a map entry, not touching this function (SOLID OCP/LSP). */
const MODE_MARKER_VARIANT: Record<DeliveryMode, MarkerVariant> = {
  "scope-driven": "pin",
  "date-driven": "pin",
  continuous: "band",
};

export interface ModeAwareDeliveryMarkerProps {
  readonly entry: RoadmapEntry;
  readonly windowStart: number;
  readonly windowEnd: number;
}

/** A commitment on the same time axis as forecast bars, but never styled as a forecast band. */
function DeclaredDateMarker({
  date,
  windowStart,
  windowEnd,
}: {
  readonly date: string;
  readonly windowStart: number;
  readonly windowEnd: number;
}): React.ReactNode {
  return (
    <div className="roadmapbar declared">
      <div className="track">
        <span
          className="declared-pin"
          role="img"
          aria-label={`Declared date: ${formatDate(date)} (unsupported forecast)`}
          style={{ left: `${pct(date, windowStart, windowEnd)}%` }}
        />
      </div>
      <div className="dates meta">
        Declared date: <b>{formatDate(date)}</b>
        <span className="unsupported-label">unsupported forecast</span>
      </div>
    </div>
  );
}

/**
 * AC3/AC4 — resolves ONE of three honest outcomes per project, never an invented date:
 *  1. no credible forecast basis → a declared-date marker if available, plus the honest
 *     unsupported state with the API's OWN reason text and the `Basis` chip.
 *  2. a forecast exists but no `deliveryMode` was ever declared → still honest-unsupported: this
 *     component cannot choose a pin or a band for a mode it was never told, and guessing one would
 *     be the same fabrication ADR-016 forbids for dates.
 *  3. both are present → dispatch through `MODE_MARKER_VARIANT` to a pin or a band.
 */
export function ModeAwareDeliveryMarker({
  entry,
  windowStart,
  windowEnd,
}: ModeAwareDeliveryMarkerProps): React.ReactNode {
  if (entry.forecast.kind !== "value") {
    return (
      <>
        {entry.targetDate !== null && (
          <DeclaredDateMarker
            date={entry.targetDate}
            windowStart={windowStart}
            windowEnd={windowEnd}
          />
        )}
        <Basis kind={basisKindOf(entry.confidenceBasis)} />
        <HonestState kind="notobs" detail={entry.forecast.reason} />
      </>
    );
  }
  if (entry.deliveryMode === null) {
    return (
      <HonestState
        kind="notobs"
        detail="Delivery mode is not declared for this project, so the timeline cannot honestly choose a fixed-date pin or a continuous band."
      />
    );
  }
  return (
    <DeliveryConfidenceBar
      p50Date={entry.forecast.value.p50Date}
      p85Date={entry.forecast.value.p85Date}
      windowStart={windowStart}
      windowEnd={windowEnd}
      confidence={
        entry.forecast.confidence.kind === "value" ? entry.forecast.confidence.confidence : null
      }
      variant={MODE_MARKER_VARIANT[entry.deliveryMode]}
    />
  );
}

export interface SwimlaneProps {
  readonly entry: RoadmapEntry;
  readonly windowStart: number;
  readonly windowEnd: number;
}

/** One project's lane (AC2: one lane per project). Presentational — props only, no fetch (AC5). */
export function Swimlane({ entry, windowStart, windowEnd }: SwimlaneProps): React.ReactNode {
  return (
    <div className="lane">
      <a className="lanehead" href={`/projects/${entry.id}`}>
        <span className="lanename">{entry.name}</span>
        <span className="lanekind">{entry.scopeType}</span>
      </a>
      <div className="lanebody">
        <ModeAwareDeliveryMarker entry={entry} windowStart={windowStart} windowEnd={windowEnd} />
        {entry.targetDate !== null && entry.forecast.kind === "value" && (
          <div className="target meta">
            Declared target: <b>{formatDate(entry.targetDate)}</b>
            {/* NX-2649: the declared commitment, reported beside the forecast and never derived
                from it in either direction — see roadmap.ts's own module comment. */}
          </div>
        )}
      </div>
    </div>
  );
}
